/**
 * useLeaguesData - Custom Hook
 * 
 * Hook personalizado que encapsula toda la lógica de obtención y gestión de datos de ligas.
 * Realiza dos peticiones al backend de forma paralela:
 * 1. getTickerLeaguesList() - Obtiene todas las ligas disponibles
 * 2. getTickerLeaguesSelected() - Obtiene las ligas actualmente seleccionadas
 * 
 * Compara los datos por ID para determinar el estado de selección (status).
 * Ordena las ligas por display_order de forma incremental.
 * 
 * @hook
 * @returns {Object} Objeto con las siguientes propiedades:
 *   - leagues {Array} Array de ligas con estructura: { id, name, know_name, display_order, status }
 *   - loading {boolean} true mientras se cargan los datos
 *   - error {string|null} Mensaje de error si algo falla, null si todo va bien
 *   - toggleLeague {Function} Función para alternar el estado de una liga por su ID
 * 
 * Comportamiento:
 * - Se ejecuta una sola vez al montar el componente
 * - Si hay error en cualquier petición, muestra estado de error
 * - Las ligas se ordenan automáticamente por display_order
 * - El toggle mantiene el orden original de las ligas
 */

import { useState, useEffect } from 'react';
import { getTickerLeaguesList, getTickerLeaguesSelected } from '../services/tickerLeagues';

export const useLeaguesData = () => {
  const [leagues, setLeagues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchLeagues = async () => {
      try {
        setLoading(true);
        // Obtener listado completo de ligas
        const listResponse = await getTickerLeaguesList();
        // Obtener ligas seleccionadas
        const selectedResponse = await getTickerLeaguesSelected();

        if (listResponse.statusCode === 200 && selectedResponse.statusCode === 200) {
          // Mapear el listado completo y comparar con seleccionadas por id
          const selectedIds = selectedResponse.data.map(league => league.id);
          const leaguesWithStatus = listResponse.data.map(league => ({
            ...league,
            status: selectedIds.includes(league.id),
          }));
          // Ordenar por display_order de menor a mayor
          leaguesWithStatus.sort((a, b) => a.display_order - b.display_order);
          setLeagues(leaguesWithStatus);
        } else {
          setError('Error al cargar las ligas');
        }
      } catch (err) {
        console.error('Error fetching leagues:', err);
        setError('Error al cargar las ligas');
      } finally {
        setLoading(false);
      }
    };

    fetchLeagues();
  }, []);

  /**
   * Alterna el estado de selección de una liga específica
   * @param {string} id - ID de la liga a alternar
   */
  const toggleLeague = (id) => {
    setLeagues(prevLeagues =>
      prevLeagues.map(league =>
        league.id === id ? { ...league, status: !league.status } : league
      )
    );
  };

  return { leagues, setLeagues, loading, error, toggleLeague };
};
